import React, { useState, useEffect, useCallback } from "react";
import { Card, Modal, message, notification } from "antd";
import ProjectsTable from "../Common/ProjectsTable";
import ProjectCreationModal from "./ProjectCreationModal";
import FinanceExportModal from "../../Finance/Common/FinanceExportModal";
import ExportProgressModal from "../../Finance/Common/ExportProgressModal";
import nodeAPI from "../../../service/ApiServiceNode";
import { applyProjectListSortParams } from "../Common/projectTableSort";
import "../project-management.scss";
import logger from "../../../utils/logger";



const Projects = ({
    assignedUserEmail = null,
    hideAddButton = false,
}) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);

    // All authenticated users can create new projects.
    // The backend POST /project endpoint only requires auth — no role restriction —
    // so the frontend gate is removed to match that policy.
    const canAddProject = () => true;

    // Get initial tab from URL params - default to 'confirmed'
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get("tab");

    // Valid tabs shown on the Projects page.
    const validTabs = ["issues", "confirmed", "lead", "internal"];
    const initialTab = validTabs.includes(tabParam) ? tabParam : "confirmed";

    const [activeTab, setActiveTab] = useState(initialTab);
    const [retainerFilter, setRetainerFilter] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);
    const [departmentFilter, setDepartmentFilter] = useState([]);
    const [projectTypeFilter, setProjectTypeFilter] = useState([]);
    const [industryFilter, setIndustryFilter] = useState([]);

    // Initialize status filter based on initial tab
    const getInitialStatusFilter = () => {
        // Getting initial status filter for tab
        if (initialTab === "confirmed") {
            return ["On-Going", "In-Planning"];
        } else if (initialTab === "lead") {
            return ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
        } else if (initialTab === "internal") {
            return [
                "Active",
                "On-Going",
                "In-Planning",
                "Completed",
                "Paused",
                "Cancelled",
                "Finished",
                "Archived",
            ];
        }
        return [];
    };

    const [statusFilter, setStatusFilter] = useState(getInitialStatusFilter());
    const [sortState, setSortState] = useState({
        orderBy: null,
        order: null,
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
    });
    const [showCreationModal, setShowCreationModal] = useState(false);

    // Export modal states (Finance pattern)
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [currentJobId, setCurrentJobId] = useState(null);

    // Vendor breakdown export modal states
    const [vendorBreakdownExportModalVisible, setVendorBreakdownExportModalVisible] = useState(false);
    const [vendorBreakdownProgressModalVisible, setVendorBreakdownProgressModalVisible] = useState(false);
    const [vendorBreakdownJobId, setVendorBreakdownJobId] = useState(null);

    // Fetch projects from API
    const fetchProjects = useCallback(async (
        tab,
        page = 1,
        retainer,
        search,
        dates,
        departments,
        projectTypes,
        industries,
        statuses,
        sort = sortState
    ) => {
        try {
            setLoading(true);

            // Build query parameters
            const params = {
                page,
                limit: pagination.limit,
            };
            applyProjectListSortParams(params, sort);

            // Add type filter based on tab
            if (tab && tab !== "all") {
                params.type = tab;
            }

            // Add retainer filter
            if (retainer === "retainer") {
                params.retainer = "true";
            } else if (retainer === "non-retainer") {
                params.retainer = "false";
            }
            // If retainer === 'all', don't add the param (show all projects)

            // Add search filter
            if (search && search.trim()) {
                params.search = search.trim();
            }

            // Add date range filter
            if (dates && dates[0]) {
                params.startDate = dates[0].format("YYYY-MM-DD");
            }
            if (dates && dates[1]) {
                params.endDate = dates[1].format("YYYY-MM-DD");
            }

            // Add department filter
            if (departments && departments.length > 0) {
                params.department_ids = departments.join(",");
            }

            // Add project type/classification filter
            if (projectTypes && projectTypes.length > 0) {
                params.classification_ids = projectTypes.join(",");
            }

            // Add industry filter
            if (industries && industries.length > 0) {
                params.industry_ids = industries.join(",");
            }

            // Add status/lead filter based on active tab
            if (statuses && statuses.length > 0) {
                // Filter - Tab and Statuses
                if (tab === "lead") {
                    // For lead tab, check if DateIssue or FinanceIssue is selected
                    const hasIssueFilter = statuses.includes('DateIssue') || statuses.includes('FinanceIssue');

                    if (hasIssueFilter) {
                        // If issue filter is selected, always send the filter
                        params.lead_list = statuses.join(",");
                    } else {
                        // For regular statuses only, check if all are selected
                        const allLeadStatuses = [
                            "1",
                            "2",
                            "3",
                            "4",
                            "5",
                            "6",
                            "7",
                            "8",
                            "9",
                        ];
                        const hasAllSelected =
                            allLeadStatuses.length === statuses.length &&
                            allLeadStatuses.every((status) =>
                                statuses.includes(status)
                            );

                        if (!hasAllSelected && statuses.length > 0) {
                            // Only send filter if not all statuses are selected
                            params.lead_list = statuses.join(",");
                        }
                    }
                } else {
                    // For other tabs, always send status_list if there are filters
                    params.status_list = statuses.join(",");
                }
            } else {
                // No status filter to apply
            }

            // Add assignedUserEmail filter if provided (for employee project view)
            let response;
            if (assignedUserEmail) {
                // Use different endpoint for employee-specific projects
                response = await nodeAPI.get(`/project/employees/${encodeURIComponent(assignedUserEmail)}/projects`, { params });
            } else {
                // Final API Request params
                response = await nodeAPI.get("/project", { params });
            }

            // Debug logging
            // API Response received

            // Handle nested response structure - API returns data.data sometimes
            let projectsData = response.data;

            // Extract pagination before/after unwrapping because the API can return
            // it either at the top level or inside data.
            const paginationData = projectsData.pagination || projectsData.data?.pagination;

            // Check for nested data.data structure
            if (projectsData && projectsData.data) {
                // Found nested data.data structure
                projectsData = projectsData.data;
            }

            // Projects data after extraction

            // Now extract projects and pagination
            if (projectsData?.projects) {
                // Setting projects
                setProjects(projectsData.projects);

                // Update pagination if provided (from top level response.data.pagination)
                if (paginationData) {
                    setPagination({
                        page: paginationData.page || page,
                        limit: paginationData.limit || 20,
                        total: paginationData.total || 0,
                    });
                }
            } else {
                // No projects found in response
                // Fallback to empty array if no projects
                setProjects([]);
                setPagination({
                    page: 1,
                    limit: 20,
                    total: 0,
                });
            }
        } catch (error) {
            logger.error("Error fetching projects:", error);
            message.error("Failed to load projects");
            setProjects([]);
        } finally {
            setLoading(false);
        }
    }, [assignedUserEmail, pagination.limit, sortState]);

    // Fetch projects on component mount and when filters change
    useEffect(() => {
        // Fetching projects with activeTab and statusFilter
        fetchProjects(
            activeTab,
            1,
            retainerFilter,
            searchText,
            dateRange,
            departmentFilter,
            projectTypeFilter,
            industryFilter,
            statusFilter
        );
    }, [
        activeTab,
        dateRange,
        departmentFilter,
        fetchProjects,
        industryFilter,
        projectTypeFilter,
        retainerFilter,
        searchText,
        statusFilter,
    ]);

    // Debounce search - trigger API call 250ms after user stops typing
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchProjects(
                activeTab,
                1,
                retainerFilter,
                searchText,
                dateRange,
                departmentFilter,
                projectTypeFilter,
                industryFilter,
                statusFilter
            );
        }, 250);

        return () => clearTimeout(delayDebounceFn);
    }, [
        activeTab,
        dateRange,
        departmentFilter,
        fetchProjects,
        industryFilter,
        projectTypeFilter,
        retainerFilter,
        searchText,
        statusFilter,
    ]);

    // Handle project creation events to switch tabs
    useEffect(() => {
        const handleProjectCreated = (event) => {
            const { targetTab } =
                event.detail;
            // Project created, switching to tab

            // Map custom tab names to actual tab keys
            const tabMapping = {
                "my-projects": "lead", // Lead projects go to lead tab
                projects: "confirmed", // Confirmed/Internal projects go to confirmed tab
                leads: "lead",
                confirmed: "confirmed",
                internal: "internal",
            };

            const actualTab = tabMapping[targetTab] || targetTab;

            // Prospect projects can still be created through their dedicated
            // workflow, but their list is intentionally hidden on this page.
            if (actualTab === "prospect") {
                return;
            }

            // Change tab if it's different
            if (actualTab !== activeTab) {
                setActiveTab(actualTab);

                // Set appropriate filters for the new tab
                let newStatusFilter = [];
                if (actualTab === "confirmed") {
                    newStatusFilter = ["On-Going", "In-Planning"];
                } else if (actualTab === "lead") {
                    newStatusFilter = [
                        "1",
                        "2",
                        "3",
                        "4",
                        "5",
                        "6",
                        "7",
                        "8",
                        "9",
                    ];
                } else if (actualTab === "internal") {
                    newStatusFilter = [
                        "Active",
                        "On-Going",
                        "In-Planning",
                        "Completed",
                        "Paused",
                        "Cancelled",
                        "Finished",
                        "Archived",
                    ];
                }

                setStatusFilter(newStatusFilter);

                // Reset pagination
                setPagination({ page: 1, limit: 20, total: 0 });

                // Refresh the projects list with the new filters after state update
                logger.debug(
                    "Switching to tab:",
                    actualTab,
                    "with status filter:",
                    newStatusFilter
                );
                setTimeout(() => {
                    logger.debug("Calling fetchProjects with:", {
                        actualTab,
                        newStatusFilter,
                    });
                    fetchProjects(
                        actualTab,
                        1,
                        retainerFilter,
                        searchText,
                        dateRange,
                        departmentFilter,
                        projectTypeFilter,
                        industryFilter,
                        newStatusFilter
                    );
                }, 200);
            } else {
                // Same tab, just refresh
                fetchProjects(
                    actualTab,
                    1,
                    retainerFilter,
                    searchText,
                    dateRange,
                    departmentFilter,
                    projectTypeFilter,
                    industryFilter,
                    statusFilter
                );
            }
        };

        // Listen for project creation events
        window.addEventListener("project-created", handleProjectCreated);

        return () => {
            window.removeEventListener("project-created", handleProjectCreated);
        };
    }, [
        activeTab,
        dateRange,
        departmentFilter,
        fetchProjects,
        industryFilter,
        projectTypeFilter,
        retainerFilter,
        searchText,
        statusFilter,
    ]);

    const handleAddProject = () => {
        setShowCreationModal(true);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on tab change

        // Clear status filter when switching tabs
        // Each tab has different status options
        setStatusFilter([]);

        // Pre-select statuses based on tab
        if (key === "confirmed") {
            // For Confirmed tab: pre-select On-Going and In-Planning
            setStatusFilter(["On-Going", "In-Planning"]);
        } else if (key === "lead") {
            // For Lead tab: pre-select all lead statuses (1-9)
            setStatusFilter(["1", "2", "3", "4", "5", "6", "7", "8", "9"]);
        } else if (key === "internal") {
            // For Internal tab: pre-select all project statuses
            setStatusFilter([
                "Active",
                "On-Going",
                "In-Planning",
                "Completed",
                "Paused",
                "Cancelled",
                "Finished",
                "Archived",
            ]);
        }
    };

    const handleRetainerChange = (value) => {
        // If clicking the same button, remove filter (set to 'all')
        // Otherwise, set the new filter
        const newFilter = retainerFilter === value ? "all" : value;
        setRetainerFilter(newFilter);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on filter change
    };

    const handlePageChange = (page) => {
        fetchProjects(
            activeTab,
            page,
            retainerFilter,
            searchText,
            dateRange,
            departmentFilter,
            projectTypeFilter,
            industryFilter,
            statusFilter
        );
    };

    const handleSortChange = useCallback((nextSort) => {
        setSortState(nextSort);
        setPagination((currentPagination) => ({
            ...currentPagination,
            page: 1,
        }));
    }, []);

    const handleSearch = useCallback(
        (value) => {
            setSearchText(value);
            setPagination({ ...pagination, page: 1 }); // Reset to first page on search
        },
        [pagination]
    );

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on date change
    };

    const handleDepartmentChange = (values) => {
        setDepartmentFilter(values);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on filter change
    };

    const handleProjectTypeChange = (values) => {
        setProjectTypeFilter(values);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on filter change
    };

    const handleIndustryChange = (values) => {
        setIndustryFilter(values);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on filter change
    };

    const handleStatusChange = (values) => {
        setStatusFilter(values);
        setPagination({ ...pagination, page: 1 }); // Reset to first page on filter change
    };

    // Enhanced export handler
    const handleExport = () => {
        // Check if prospect or issues tab - no export available
        if (activeTab === 'prospect' || activeTab === 'issues') {
            const tabName = activeTab === 'prospect' ? 'Prospect' : 'Issues';
            Modal.info({
                title: 'Export Not Available',
                content: `Export functionality is not available for ${tabName} projects.`,
                okText: 'OK',
                okButtonProps: {
                    style: {
                        backgroundColor: '#FED230',
                        borderColor: '#FED230',
                        color: '#1B1B1B',
                        fontWeight: 'bold'
                    }
                }
            });
            return;
        }

        setExportModalVisible(true);
    };

    const handleExportConfirm = async (exportLocation) => {
        try {
            setExportModalVisible(false);

            // Build query parameters (same as fetchProjects)
            const params = new URLSearchParams();

            // Add type filter based on tab
            if (activeTab && activeTab !== "all") {
                params.append("type", activeTab);
            }

            // Add retainer filter
            if (retainerFilter === "retainer") {
                params.append("retainer", "true");
            } else if (retainerFilter === "non-retainer") {
                params.append("retainer", "false");
            }

            // Add search filter
            if (searchText && searchText.trim()) {
                params.append("search", searchText.trim());
            }

            // Add date range filter
            if (dateRange && dateRange[0]) {
                params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
            }
            if (dateRange && dateRange[1]) {
                params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
            }

            // Add other filters
            if (departmentFilter && departmentFilter.length > 0) {
                params.append("department_ids", departmentFilter.join(","));
            }
            if (projectTypeFilter && projectTypeFilter.length > 0) {
                params.append("classification_ids", projectTypeFilter.join(","));
            }
            if (industryFilter && industryFilter.length > 0) {
                params.append("industry_ids", industryFilter.join(","));
            }

            // Add status filters
            if (statusFilter && statusFilter.length > 0) {
                if (activeTab === "lead") {
                    const leadStatuses = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
                    if (statusFilter.length < leadStatuses.length) {
                        params.append("lead_list", statusFilter.join(","));
                    }
                } else {
                    params.append("status_list", statusFilter.join(","));
                }
            }

            // Add export target parameter
            params.append("_target", exportLocation);

            // Use async method with progress tracking
            message.loading({
                content: "Starting export...",
                key: "export-start",
                duration: 2,
            });

            // Determine endpoint based on project type
            let endpoint = '/report/projects/confirmed/exports';
            if (activeTab === 'lead') {
                endpoint = '/report/projects/lead/exports';
            } else if (activeTab === 'internal') {
                endpoint = '/report/projects/internal/exports';
            }

            // Start export job
            const response = await nodeAPI.post(
                `${endpoint}?${params.toString()}`
            );

            const jobId = response.data?.data?.jobId || response.data?.jobId;

            if (jobId) {
                // Show progress modal
                setCurrentJobId(jobId);
                setProgressModalVisible(true);
            } else {
                message.error({
                    content: "Failed to start export. No job ID returned.",
                    key: "export-start",
                });
            }
        } catch (error) {
            logger.error("Export failed:", error);
            message.error({
                content: "Failed to start export. Please try again.",
                key: "export-start",
            });
        }
    };

    const handleProgressComplete = (results) => {
        // Close progress modal
        setProgressModalVisible(false);
        setCurrentJobId(null);

        // Handle based on export type
        if (results?.fileUrl) {
            // Google Drive export - auto-redirect and show success message
            message.success({
                content: (
                    <span>
                        Export completed successfully! Opening Google Drive file...{' '}
                        <a href={results.fileUrl} target="_blank" rel="noopener noreferrer">
                            Click here if not opened
                        </a>
                    </span>
                ),
                duration: 10,
            });

            // Auto-redirect to Google Drive after short delay
            setTimeout(() => {
                window.open(results.fileUrl, '_blank', 'noopener,noreferrer');
            }, 500);
        } else if (results?.downloadBuffer) {
            // Local export - trigger download
            try {
                // Convert base64 back to binary
                const binaryString = atob(results.downloadBuffer);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const blob = new Blob([bytes], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = results.filename || `Projects_${activeTab}_Export_${
                    new Date().toISOString().split("T")[0]
                }.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                message.success({
                    content: `Export downloaded successfully! ${results?.totalRecords || 0} records exported.`,
                    duration: 5,
                });
            } catch (error) {
                logger.error('Error downloading file:', error);
                message.error({
                    content: 'Failed to download file. Please try again.',
                    duration: 5,
                });
            }
        } else {
            message.success({
                content: `Export completed! ${results?.totalRecords || 0} records exported.`,
                duration: 5,
            });
        }
    };

    const handleProgressCancel = () => {
        setProgressModalVisible(false);
        setCurrentJobId(null);
    };

    const handleExportCancel = () => {
        setExportModalVisible(false);
    };

    // Vendor Breakdown Export handler - Show modal for location selection
    const handleVendorBreakdownExport = () => {
        // Only available for confirmed tab
        if (activeTab !== 'confirmed') {
            Modal.info({
                title: 'Export Not Available',
                content: 'Detailed confirmed report with vendors is only available for Confirmed projects.',
                okText: 'OK',
                okButtonProps: {
                    style: {
                        backgroundColor: '#FED230',
                        borderColor: '#FED230',
                        color: '#1B1B1B',
                        fontWeight: 'bold'
                    }
                }
            });
            return;
        }

        setVendorBreakdownExportModalVisible(true);
    };

    const handleVendorBreakdownExportConfirm = async (exportLocation) => {
        try {
            setVendorBreakdownExportModalVisible(false);

            // Build query parameters (same as regular export)
            const params = new URLSearchParams();

            // Add type filter
            params.append("type", "confirmed");

            // Add retainer filter
            if (retainerFilter === "retainer") {
                params.append("retainer", "true");
            } else if (retainerFilter === "non-retainer") {
                params.append("retainer", "false");
            }

            // Add search filter
            if (searchText && searchText.trim()) {
                params.append("search", searchText.trim());
            }

            // Add date range filter
            if (dateRange && dateRange[0]) {
                params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
            }
            if (dateRange && dateRange[1]) {
                params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
            }

            // Add other filters
            if (departmentFilter && departmentFilter.length > 0) {
                params.append("department_ids", departmentFilter.join(","));
            }
            if (projectTypeFilter && projectTypeFilter.length > 0) {
                params.append("classification_ids", projectTypeFilter.join(","));
            }
            if (industryFilter && industryFilter.length > 0) {
                params.append("industry_ids", industryFilter.join(","));
            }

            // Add status filters
            if (statusFilter && statusFilter.length > 0) {
                params.append("status_list", statusFilter.join(","));
            }

            // Add export target parameter
            params.append("_target", exportLocation);

            // Start export job (no notification needed - progress modal will show)
            const response = await nodeAPI.post(
                `/report/projects/confirmed-vendor-breakdown/exports?${params.toString()}`
            );

            const jobId = response.data?.data?.jobId || response.data?.jobId;

            if (jobId) {
                // Show progress modal
                setVendorBreakdownJobId(jobId);
                setVendorBreakdownProgressModalVisible(true);
            } else {
                notification.error({
                    message: 'Export Failed',
                    description: "Failed to start export. No job ID returned.",
                    duration: 3
                });
            }
        } catch (error) {
            logger.error("Vendor breakdown export failed:", error);
            notification.error({
                message: 'Export Failed',
                description: "Failed to start vendor breakdown export. Please try again.",
                duration: 5
            });
        }
    };

    const handleVendorBreakdownExportCancel = () => {
        setVendorBreakdownExportModalVisible(false);
    };

    const handleVendorBreakdownProgressComplete = (results) => {
        setVendorBreakdownProgressModalVisible(false);
        setVendorBreakdownJobId(null);

        // Handle based on export type
        if (results?.target === 'google' && results?.webViewLink) {
            // Google Drive export - auto-redirect and show success message
            notification.success({
                message: 'Export Complete',
                description: (
                    <span>
                        Export completed successfully! Opening Google Drive file...{' '}
                        <a href={results.webViewLink} target="_blank" rel="noopener noreferrer">
                            Click here if it doesn&apos;t open automatically
                        </a>
                    </span>
                ),
                duration: 8
            });

            setTimeout(() => {
                window.open(results.webViewLink, '_blank');
            }, 500);

        } else if (results?.downloadBuffer) {
            // Local export - trigger download
            try {
                const binaryString = atob(results.downloadBuffer);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = results.filename || `Confirmed_Projects_Vendor_Breakdown_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                notification.success({
                    message: 'Export Complete',
                    description: `Export downloaded successfully! ${results?.totalRecords || 0} records exported.`,
                    duration: 5
                });
            } catch (error) {
                logger.error('Error downloading file:', error);
                notification.error({
                    message: 'Download Failed',
                    description: 'Failed to download file. Please try again.',
                    duration: 5
                });
            }
        } else {
            notification.success({
                message: 'Export Complete',
                description: `Export completed! ${results?.totalRecords || 0} records exported.`,
                duration: 5
            });
        }
    };

    const handleVendorBreakdownProgressCancel = () => {
        setVendorBreakdownProgressModalVisible(false);
        setVendorBreakdownJobId(null);
    };

    return (
        <div className="wrapper-grey">
            <Card>
                <ProjectsTable
                    projects={projects}
                    loading={loading}
                    title="PROJECTS"
                    showAddButton={!hideAddButton && canAddProject()}
                    onAddProject={handleAddProject}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    showProspectTab={false}
                    retainerFilter={retainerFilter}
                    onRetainerChange={handleRetainerChange}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    sortState={sortState}
                    onSortChange={handleSortChange}
                    onRefresh={() =>
                        fetchProjects(activeTab, pagination.page)
                    }
                    searchText={searchText}
                    onSearch={handleSearch}
                    dateRange={dateRange}
                    onDateRangeChange={handleDateRangeChange}
                    departmentFilter={departmentFilter}
                    onDepartmentChange={handleDepartmentChange}
                    projectTypeFilter={projectTypeFilter}
                    onProjectTypeChange={handleProjectTypeChange}
                    industryFilter={industryFilter}
                    onIndustryChange={handleIndustryChange}
                    statusFilter={statusFilter}
                    onStatusChange={handleStatusChange}
                    onExport={handleExport}
                    onVendorBreakdownExport={handleVendorBreakdownExport}
                />
            </Card>
            <ProjectCreationModal
                open={showCreationModal}
                onClose={() => {
                    setShowCreationModal(false);
                    // Refresh projects list after closing modal
                    fetchProjects(activeTab, pagination.page);
                }}
            />

            {/* Export Modal - Using reusable FinanceExportModal */}
            <FinanceExportModal
                visible={exportModalVisible}
                onCancel={handleExportCancel}
                onExport={handleExportConfirm}
                title="Export Projects"
            />

            {/* Export Progress Modal */}
            <ExportProgressModal
                visible={progressModalVisible}
                jobId={currentJobId}
                onComplete={handleProgressComplete}
                onCancel={handleProgressCancel}
                title="Projects Export"
                statusEndpoint={`/report/projects/${activeTab}/exports`}
                cancelEndpoint={`/report/projects/${activeTab}/exports`}
            />

            {/* Vendor Breakdown Export Modal */}
            <FinanceExportModal
                visible={vendorBreakdownExportModalVisible}
                onCancel={handleVendorBreakdownExportCancel}
                onExport={handleVendorBreakdownExportConfirm}
                title="Export Detailed Confirmed Report with Vendors"
            />

            {/* Vendor Breakdown Export Progress Modal */}
            <ExportProgressModal
                visible={vendorBreakdownProgressModalVisible}
                jobId={vendorBreakdownJobId}
                onComplete={handleVendorBreakdownProgressComplete}
                onCancel={handleVendorBreakdownProgressCancel}
                title="Vendor Breakdown Export"
                statusEndpoint="/report/projects/confirmed-vendor-breakdown/exports"
                cancelEndpoint="/report/projects/confirmed-vendor-breakdown/exports"
            />
        </div>
    );
};

export default Projects;
